import { useCallback, useEffect, useState } from 'react';

const THEME_STORAGE_KEY = 'edsupp-theme';
const THEME_CHANGE_EVENT = 'edsupp:theme-change';

const getInitialDarkMode = () => {
  if (typeof window === 'undefined') return false;

  const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
  if (savedTheme === 'dark') return true;
  if (savedTheme === 'light') return false;

  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const applyThemeClass = (isDarkModeEnabled) => {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', isDarkModeEnabled);
};

const useDarkMode = () => {
  const [isDarkMode, setIsDarkMode] = useState(getInitialDarkMode);

  useEffect(() => {
    applyThemeClass(isDarkMode);
    localStorage.setItem(THEME_STORAGE_KEY, isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Keep multiple hook instances in sync — the toggle may live in a sidebar
  // while the page background and other components also read the theme.
  useEffect(() => {
    const handleChange = (event) => {
      const next = Boolean(event.detail);
      setIsDarkMode((prev) => (prev === next ? prev : next));
    };
    window.addEventListener(THEME_CHANGE_EVENT, handleChange);
    return () => window.removeEventListener(THEME_CHANGE_EVENT, handleChange);
  }, []);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode((prev) => {
      const next = !prev;
      window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: next }));
      return next;
    });
  }, []);

  return { isDarkMode, toggleDarkMode };
};

export default useDarkMode;